// === DEVICE TIER (Chromebook optimization) ===
// Probe a disposable WebGL context before creating the real renderer. GPU names
// and conservative WebGL caps catch Chromebook-class devices even when memory or
// CPU hints are missing, while navigator hints remain the fallback.
//
// TIER is the boot-time guess that sizes every budget in the game (render
// scale, shadow cadence, particle pools, creature caps). The design plan calls
// for a runtime quality governor on top of this — when that lands, this stays
// the initial guess and measured frame time drives the levers.
function getWebGLTierHints() {
  const hints = {
    renderer: '',
    vendor: '',
    maxTextureSize: 0,
    maxRenderbufferSize: 0,
  };
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false })
    || canvas.getContext('webgl', { failIfMajorPerformanceCaveat: false })
    || canvas.getContext('experimental-webgl', { failIfMajorPerformanceCaveat: false });

  if (!gl) return hints;

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (debugInfo) {
    hints.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
    hints.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
  }
  hints.renderer ||= gl.getParameter(gl.RENDERER) || '';
  hints.vendor ||= gl.getParameter(gl.VENDOR) || '';
  hints.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;
  hints.maxRenderbufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || 0;

  const loseContext = gl.getExtension('WEBGL_lose_context');
  if (loseContext) loseContext.loseContext();
  return hints;
}

function isLowEndRenderer(renderer, vendor = '') {
  const gpu = `${vendor} ${renderer}`.toLowerCase();
  return /swiftshader|llvmpipe|software rasterizer/.test(gpu)
    || /intel.*(?:uhd graphics\s*(?:600|605)|hd graphics\s*(?:400|500|505|510|515)\b)/.test(gpu)
    || /mali-(?:4\d{2}|t(?:720|760|820|830)|g(?:31|51|52))\b/.test(gpu);
}

function detectTier() {
  const params = new URLSearchParams(location.search);
  const forcedTier = params.get('tier');
  if (params.get('low') === '1' || params.get('chromebook') === '1') return 'low';
  if (forcedTier === 'low' || forcedTier === 'mid' || forcedTier === 'high') return forcedTier;

  const mem = navigator.deviceMemory || 8;
  const cores = navigator.hardwareConcurrency || 4;
  const gl = getWebGLTierHints();
  const cap = Math.min(
    gl.maxTextureSize || Number.POSITIVE_INFINITY,
    gl.maxRenderbufferSize || Number.POSITIVE_INFINITY
  );

  if (isLowEndRenderer(gl.renderer, gl.vendor)) return 'low';
  if (cap <= 4096) return 'low';
  if (mem <= 4 || cores <= 4) return 'low';
  if (cap <= 8192 && (mem <= 8 || cores <= 6)) return 'low';
  if (mem <= 8 || cap <= 8192) return 'mid';
  return 'high';
}
export const TIER = detectTier();
export const LOW = TIER === 'low';
export const ULTRA_LOW_SHADOWS = LOW;
// Low-power CSS strips backdrop-filter/shadows from the HUD — blurred,
// translucent UI over WebGL costs fill-rate and extra layers on weak GPUs.
if (LOW) document.documentElement.classList.add('low-power');
const _tierParams = new URLSearchParams(location.search);
const _tierOverride = _tierParams.get('tier')
  || (_tierParams.get('low') === '1' ? 'low'
    : _tierParams.get('chromebook') === '1' ? 'chromebook' : 'none');
console.log(`[perf] tier=${TIER} mem=${navigator.deviceMemory ?? '?'}GB cores=${navigator.hardwareConcurrency} override=${_tierOverride}`);

// Tiny runtime texture cache. Chromebook mode uses smaller procedural canvases
// and caches repeated sprite variants so startup does less synchronous 2D work.
const textureCache = new Map();
export function cachedTexture(key, create) {
  if (textureCache.has(key)) return textureCache.get(key);
  const tex = create();
  textureCache.set(key, tex);
  return tex;
}
